import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, ShieldPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import PermissionMatrixEditor from './PermissionMatrixEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin-defined roles: named permission templates reusable when creating users.
export const CustomRolesManager = ({ customRoles, onChanged }) => {
  const [dialog, setDialog] = useState(null); // null | {mode:'create'} | {mode:'edit', role}
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', apply_to_users: false });
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  const open = (mode, role) => {
    setForm({ name: role?.name || '', description: role?.description || '', apply_to_users: false });
    setMatrix(role?.permissions_v2 || {});
    setDialog({ mode, role });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Role name is required');
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description, permissions_v2: matrix, apply_to_users: form.apply_to_users };
      if (dialog.mode === 'create') {
        await axios.post(`${API_URL}/api/admin/custom-roles`, payload);
        toast.success('Custom role created — it can now be assigned when creating users');
      } else {
        const r = await axios.put(`${API_URL}/api/admin/custom-roles/${dialog.role.id}`, payload);
        toast.success(`Role updated${r.data.users_updated ? ` — applied to ${r.data.users_updated} user(s)` : ''}`);
      }
      setDialog(null);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save role'); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/custom-roles/${deleting.id}`);
      toast.success('Custom role deleted');
      setDeleting(null);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete role'); }
  };

  const modulesGranted = (r) => Object.values(r.permissions_v2 || {}).filter((b) => b && b.view).length;

  return (
    <Card data-testid="custom-roles-manager">
      <CardHeader className="pb-3 flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="font-heading text-base flex items-center gap-2"><ShieldPlus className="h-4 w-4" /> Custom Roles</CardTitle>
          <CardDescription>Create named permission templates. Assigning a custom role to a user auto-fills their access to every tab — view/edit per module can then be fine-tuned per user.</CardDescription>
        </div>
        <Button size="sm" onClick={() => open('create')} data-testid="create-role-btn"><Plus className="h-4 w-4 mr-1" /> New Role</Button>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full">
          <thead><tr>{['Role', 'Description', 'Modules Granted', 'Users', ''].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase bg-muted border-b">{h}</th>)}</tr></thead>
          <tbody>
            {customRoles.map((r) => (
              <tr key={r.id} className="hover:bg-muted/40" data-testid={`role-row-${r.key}`}>
                <td className="px-3 py-2 border-b text-sm font-medium">{r.name}<p className="text-[10px] text-muted-foreground font-mono">{r.key}</p></td>
                <td className="px-3 py-2 border-b text-xs text-muted-foreground">{r.description || '—'}</td>
                <td className="px-3 py-2 border-b text-xs"><Badge variant="outline" className="bg-blue-100 text-blue-800">{modulesGranted(r)} modules</Badge></td>
                <td className="px-3 py-2 border-b text-xs">{r.users_count || 0}</td>
                <td className="px-3 py-2 border-b text-right space-x-1">
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => open('edit', r)} data-testid={`edit-role-${r.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-red-600" onClick={() => setDeleting(r)} data-testid={`delete-role-${r.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {customRoles.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No custom roles yet — create one to reuse a permission template across users</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog?.mode === 'create' ? 'Create Custom Role' : `Edit Role — ${dialog?.role?.name}`}</DialogTitle>
            <DialogDescription>Tick the tabs/modules this role can access, and which actions (view, create, edit, delete, export, approve) it grants by default.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fees Clerk" data-testid="role-name-input" /></div>
              <div><Label>Description (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc-input" /></div>
            </div>
            <PermissionMatrixEditor role={dialog?.role?.key || ''} value={matrix} onChange={setMatrix} autoFillOnRoleChange={false} />
            {dialog?.mode === 'edit' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.apply_to_users} onChange={(e) => setForm({ ...form, apply_to_users: e.target.checked })} data-testid="role-apply-users" />
                Also apply the updated template to the {dialog.role.users_count || 0} user(s) currently on this role
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="role-save-btn">{saving ? 'Saving…' : dialog?.mode === 'create' ? 'Create Role' : 'Save Role'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Roles in use by users cannot be deleted — reassign those users first. This does not affect users' already-saved permissions.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="confirm-delete-role">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CustomRolesManager;
